import { loadPdfJs } from "@/components/PdfCanvas";
import { getApiUrl } from "@/lib/config";

/** The code printed on every page of a signed PDF. */
export const CODE_RX = /FSIG-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}/;

export type VerifyStatus = "authentic" | "modified" | "unknown" | "registered";

export interface VerifiedSigner {
  name: string;
  email: string;
  cpf?: string;
  phone?: string;
  status: "signed" | "pending" | "declined" | "viewed";
  signedAt?: string;
  ipAddress?: string;
  signatureMethod: string;
}

export interface VerifyResult {
  status: VerifyStatus;
  completed: boolean;
  matchedVersion?: "final" | "signed-partial" | "original" | null;
  signedCount: number;
  totalSigners: number;
  documentName?: string;
  verificationCode?: string;
  createdAt?: string;
  signedAt?: string;
  certificateUrl?: string;
  signers: VerifiedSigner[];
  chatToken?: string | null;
}

export class CaptchaRequiredError extends Error {
  constructor(public siteKey: string | null) { super("captcha_required"); }
}

/** SHA-256 of the bytes, lowercase hex, computed in the browser (the file never leaves the device). */
export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Looks for the FSIG code in the PDF text (footer of every page): first page, last page, then the rest, up to 30
 * pages. Null when the PDF has no text layer (scan) or no code.
 */
export async function findVerificationCode(bytes: ArrayBuffer): Promise<string | null> {
  const lib = await loadPdfJs();
  const doc = await lib.getDocument({ data: new Uint8Array(bytes.slice(0)) }).promise;
  try {
    const n: number = doc.numPages;
    const order = [1, n, ...Array.from({ length: Math.min(n, 30) }, (_, i) => i + 1)].filter((p, i, a) => p >= 1 && a.indexOf(p) === i);
    for (const pageNo of order) {
      const page = await doc.getPage(pageNo);
      const content = await page.getTextContent();
      const text = content.items.map((it: any) => it.str ?? "").join(" ");
      const m = text.match(CODE_RX);
      if (m) return m[0];
    }
    return null;
  } finally {
    try { await doc.destroy(); } catch { /* ignore */ }
  }
}

/** One request with the code and/or hash; a 428 means the API wants a captcha first. */
export async function verifyCheck(input: { code?: string | null; hash?: string | null; lang: string }, captchaToken?: string): Promise<VerifyResult> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (captchaToken) headers["X-Turnstile-Token"] = captchaToken;
  const res = await fetch(getApiUrl("/api/verify/check"), {
    method: "POST",
    headers,
    body: JSON.stringify({ code: input.code || null, hash: input.hash || null, lang: input.lang }),
  });
  if (res.status === 428) {
    const body = await res.json().catch(() => ({}));
    throw new CaptchaRequiredError(body.turnstileSiteKey ?? null);
  }
  if (!res.ok) throw new Error(`verify_failed_${res.status}`);
  const json = await res.json();
  return (json.data ?? json) as VerifyResult;
}

export interface VerifyChatReply { conversationId: string; outcome: string; reply: { text: string; mood?: string | null } }

export async function verifyChat(input: { token: string; conversationId: string | null; message: string; lang: string }, captchaToken?: string): Promise<VerifyChatReply> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (captchaToken) headers["X-Turnstile-Token"] = captchaToken;
  const res = await fetch(getApiUrl("/api/verify/chat"), { method: "POST", headers, body: JSON.stringify(input) });
  if (res.status === 428) {
    const body = await res.json().catch(() => ({}));
    throw new CaptchaRequiredError(body.turnstileSiteKey ?? null);
  }
  if (!res.ok) throw new Error(`chat_failed_${res.status}`);
  return res.json();
}

let turnstileScript: Promise<void> | null = null;
function loadTurnstile(): Promise<void> {
  if (turnstileScript) return turnstileScript;
  turnstileScript = new Promise<void>((resolve, reject) => {
    if ((window as any).turnstile) { resolve(); return; }
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => { turnstileScript = null; reject(new Error("turnstile_load_failed")); };
    document.head.appendChild(s);
  });
  return turnstileScript;
}

/**
 * Solves the invisible Cloudflare Turnstile once and returns its token (the API verifies it and clears the gate
 * for an hour). The widget is configured as invisible/managed in the Cloudflare dashboard; nothing to click normally.
 */
export async function solveCaptcha(siteKey: string): Promise<string> {
  await loadTurnstile();
  const turnstile = (window as any).turnstile;
  const host = document.createElement("div");
  host.className = "fixed bottom-4 right-4 z-[100]";
  document.body.appendChild(host);
  try {
    return await new Promise<string>((resolve, reject) => {
      const id = turnstile.render(host, {
        sitekey: siteKey,
        appearance: "interaction-only",
        callback: (token: string) => { resolve(token); setTimeout(() => turnstile.remove(id), 500); },
        "error-callback": () => reject(new Error("turnstile_error")),
        "expired-callback": () => reject(new Error("turnstile_expired")),
      });
    });
  } finally {
    setTimeout(() => host.remove(), 1500);
  }
}
